<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount } from "vue";
import { breakpointsBootstrapV5, useBreakpoints, useEventListener } from "@vueuse/core";
import { TuBasePopup, TuButton, ButtonSeverity } from "@tinyurldev/tu-ui-vue";
import { FocusTrap } from "focus-trap-vue";

import { useTranslation } from "@Resources/js/composables";
import XOverlay from "@Modules/shared/store/XOverlay";
import { logError } from "@Resources/js/helpers/logger";
import type { TeamUser } from "@Modules/teams/Data/Models/TeamUserModel";

//
// Setup
const overlay = XOverlay();
const { __ } = useTranslation();

//
// Props
interface Props {
  user: TeamUser;
  deactivateUser: () => Promise<boolean>;
}

const props = defineProps<Props>();

//
// Data
const isBusy = ref(false);

//
// Computed
const breakpoints = useBreakpoints(breakpointsBootstrapV5);
const isMobileView = breakpoints.smallerOrEqual("xl");

//
// Methods
function close(): void {
  if (isBusy.value) {
    return;
  }

  overlay.hide();
}

async function confirm(): Promise<void> {
  if (isBusy.value) {
    return;
  }

  isBusy.value = true;

  try {
    if (await props.deactivateUser()) {
      overlay.hide();
    }
  } catch {
    logError(`[DeactivateUserModal]: User ${props.user.email} could not be deactivated.`);
  } finally {
    isBusy.value = false;
  }
}

useEventListener(window, "keydown", (event: KeyboardEvent) => {
  if (event.key === "Escape") {
    close();
  }
});

onMounted(() => {
  document.body.style.overflow = "hidden";
});

onBeforeUnmount(() => {
  document.body.style.overflow = "";
});

//
// Exposed Test Vars
defineExpose({ isBusy, close, confirm });
</script>

<template>
  <div
    class="deactivate-user-modal-wrapper _deactivate_modal"
    :class="{ 'deactivate-user-modal-wrapper--mobile': isMobileView }"
    @click.self="close"
  >
    <FocusTrap :escape-deactivates="false">
      <TuBasePopup
        id="deactivate-user-modal"
        tabindex="-1"
        class="deactivate-user-modal _deactivate_popup"
        :class="{ 'deactivate-user-modal--mobile': isMobileView }"
        :is-closeable="!isMobileView"
        @close="close"
      >
        <template #title>
          <span class="_title deactivate-user-modal__title">
            {{ __("organization_users_settings.deactivate_user_modal.title") }}
          </span>
        </template>

        <template #content>
          <p class="_message deactivate-user-modal__message">
            {{ __("organization_users_settings.deactivate_user_modal.confirm_message", { name: user.name }) }}
          </p>
        </template>

        <template #footer>
          <div class="deactivate-user-modal__footer">
            <TuButton
              v-if="!isMobileView"
              class="_cancel"
              data-test-id="deactivate-user-cancel"
              :label="__('organization_users_settings.deactivate_user_modal.cancel_button')"
              is-outlined
              :severity="ButtonSeverity.CANCEL"
              :is-disabled="isBusy"
              @click="close"
            />
            <TuButton
              class="_confirm"
              data-test-id="deactivate-user-submit"
              :label="__('organization_users_settings.deactivate_user_modal.submit_button')"
              :severity="ButtonSeverity.ALERT"
              :is-block="isMobileView"
              :is-loading="isBusy"
              :is-disabled="isBusy"
              @click="confirm"
            />
          </div>
        </template>
      </TuBasePopup>
    </FocusTrap>
  </div>
</template>

<style lang="scss" scoped>
@use "@tinyurldev/tu-ui-vue/scss/settings/typography.scss" as typography;
@use "@tinyurldev/tu-ui-vue/scss/settings/position.scss" as position;
@use "@tinyurldev/tu-ui-vue/scss/settings/spacing.scss" as spacing;
@use "@tinyurldev/tu-ui-vue/scss/settings/colors.scss" as color;

.deactivate-user-modal-wrapper {
  position: fixed;
  inset: 0;
  z-index: position.$highest-z-index;

  display: flex;
  align-items: center;
  justify-content: center;
  padding: spacing.$xxs;

  background-color: color.$main-overlay;

  &--mobile {
    padding: 0;
  }
}

.deactivate-user-modal {
  max-width: 600px;

  &__title {
    font-size: typography.$h5-font-size;
    font-weight: typography.$font-weight-bold;
    line-height: typography.$h5-line-height;
  }

  &__message {
    margin: 0;

    font-size: typography.$base-font-size;
    font-weight: typography.$font-weight-medium;
    line-height: typography.$base-line-height;
  }

  &__footer {
    display: flex;
    gap: spacing.$xxs;
    align-items: end;
    justify-content: flex-end;
  }

  &--mobile {
    margin-top: auto;
  }
}
</style>
